import React, { useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/Dialog';
import { ScrollArea } from '../components/ScrollArea';
import { HelpCircle, BookOpen, PhoneCall, Mail, MessageSquare, FileText, Video, ExternalLink, Sparkles, Wrench, ShieldCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';
const faqs = [{
  q: 'Bagaimana cara mengubah peran (role) saya?',
  a: 'Klik avatar di pojok kanan atas → pilih "Ubah Peran" → pilih peran simulasi. Tingkat akses panel akan menyesuaikan otomatis.'
}, {
  q: 'Apa arti Threat Level "Waspada"?',
  a: 'Threat Level mencerminkan agregasi skor dari Threat Intel, Sentiment Score, dan Bot Activity. Waspada = 60–75. Di atas 75 = SIAGA. Di atas 90 = KRITIS.'
}, {
  q: 'Bagaimana cara melaporkan konten berbahaya ke platform?',
  a: 'Buka modul "Platform Reporting", klik "Buat Laporan Baru", isi formulir & lampirkan bukti. Status akan ditrack otomatis.'
}, {
  q: 'Apakah data saya aman?',
  a: 'Sistem ini Zero Trust + Air-Gapped, mengikuti standar UU PDP 27/2022 & UU ITE. Semua akses tercatat di Audit Log dan dienkripsi AES-256.'
}, {
  q: 'Bagaimana cara ekspor laporan?',
  a: 'Setiap halaman modul memiliki tombol "Ekspor" di kanan atas. Format default adalah PDF dengan watermark klasifikasi.'
}, {
  q: 'Apa yang dimaksud "IPOLEKSOSBUDHANKAM"?',
  a: 'Ideologi, Politik, Ekonomi, Sosial Budaya, dan Pertahanan Keamanan — kerangka stabilitas nasional yang dipantau secara digital.'
}];
const releaseNotes = [{
  version: 'v4.1.0',
  date: '20 Mei 2026',
  tag: 'CURRENT',
  sections: [{
    type: 'Added',
    icon: Plus,
    color: 'text-emerald-600',
    items: ['Modul Presidential Program War Room untuk 5 program prioritas (MBG, BBM, IKN, BANSOS, NIKEL)', 'Crisis Simulation Engine dengan 4 skenario nasional', 'Hybrid Threats correlation matrix lintas-vektor', 'Narrative Calendar prediktif 14 hari']
  }, {
    type: 'Improved',
    icon: Sparkles,
    color: 'text-blue-600',
    items: ['Executive Dashboard: Top 8 Trending Issues table menggantikan Issue Radar', 'Actor Network: visualisasi graf jaringan dengan 5 cluster utama', 'Deepfake detection: dukungan multi-format (MP4, WAV, JPEG)']
  }, {
    type: 'Security',
    icon: ShieldCheck,
    color: 'text-purple-600',
    items: ['Audit log retention diperpanjang ke 365 hari (UU PDP 27/2022)', 'MFA wajib untuk role Presiden, Wapres, Kepala BSSN']
  }]
}, {
  version: 'v4.0.2',
  date: '02 April 2026',
  tag: '',
  sections: [{
    type: 'Fixed',
    icon: Wrench,
    color: 'text-amber-600',
    items: ['Perbaikan latency stream sentiment data di jam puncak (19:00–22:00 WIB)', 'Bug filter regional pada Geospatial Heat Map saat zoom level > 10', 'Notifikasi WhatsApp tidak terkirim untuk role Menko']
  }]
}, {
  version: 'v4.0.0',
  date: '15 Januari 2026',
  tag: 'MAJOR',
  sections: [{
    type: 'Added',
    icon: Plus,
    color: 'text-emerald-600',
    items: ['Rilis perdana GARUDA SHIELD C4I — pengganti SIBER-1', 'Integrasi penuh dengan Astagatra Digital framework', 'Foreign Influence Operations dashboard', 'Mock API + PubSub untuk notifikasi cross-page']
  }, {
    type: 'Security',
    icon: ShieldCheck,
    color: 'text-purple-600',
    items: ['Arsitektur Zero Trust + Air-Gapped sesuai standar BSSN', 'Enkripsi AES-256 untuk semua data at-rest']
  }]
}];
export function Help() {
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  return <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6" data-id="element-1921">
      <PageHeader group="Sistem" title="Bantuan & Dokumentasi" description="Panduan penggunaan, FAQ, dan kontak helpdesk untuk sistem GARUDA SHIELD C4I." icon={HelpCircle} data-id="element-1922" />

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1923">
        {[{
        icon: BookOpen,
        title: 'Panduan Pengguna',
        desc: 'Manual lengkap PDF',
        action: () => toast.info('Mengunduh panduan pengguna...')
      }, {
        icon: Video,
        title: 'Video Tutorial',
        desc: '12 video pelatihan',
        action: () => toast.info('Membuka galeri video...')
      }, {
        icon: FileText,
        title: 'Release Notes',
        desc: 'v4.1.0 · Mei 2026',
        action: () => setReleaseNotesOpen(true)
      }, {
        icon: ExternalLink,
        title: 'Portal BSSN',
        desc: 'Helpdesk eksternal',
        action: () => toast.info('Membuka portal BSSN...')
      }].map(c => <button key={c.title} onClick={c.action} className="text-left p-4 rounded-lg border border-border/50 bg-card hover:border-primary/40 hover:bg-primary/5 transition group" data-id="element-1924">
            <c.icon className="size-5 text-primary mb-3" data-id="element-1925" />
            <p className="font-bold text-sm" data-id="element-1926">{c.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5" data-id="element-1927">{c.desc}</p>
          </button>)}
      </div>

      {/* FAQ + Contact */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-id="element-1928">
        <Card className="lg:col-span-8 bg-card border-border/50" data-id="element-1929">
          <CardContent className="p-6" data-id="element-1930">
            <h3 className="font-heading text-base font-bold mb-1" data-id="element-1931">
              Pertanyaan Umum (FAQ)
            </h3>
            <p className="text-xs text-muted-foreground mb-4" data-id="element-1932">
              Jawaban cepat untuk pertanyaan yang sering diajukan.
            </p>
            <div className="space-y-2" data-id="element-1933">
              {faqs.map((f, i) => <details key={i} className="group rounded-lg border border-border/50 bg-muted/20 open:bg-muted/40 transition" data-id="element-1934">
                  <summary className="cursor-pointer p-3.5 flex items-start gap-3 select-none" data-id="element-1935">
                    <Badge variant="outline" className="font-mono text-[10px] shrink-0" data-id="element-1936">
                      Q{i + 1}
                    </Badge>
                    <span className="text-sm font-medium flex-1" data-id="element-1937">{f.q}</span>
                    <span className="text-muted-foreground group-open:rotate-45 transition-transform text-lg leading-none shrink-0" data-id="element-1938">
                      +
                    </span>
                  </summary>
                  <div className="px-3.5 pb-3.5 pl-[68px] text-xs text-muted-foreground leading-relaxed" data-id="element-1939">
                    {f.a}
                  </div>
                </details>)}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 bg-card border-border/50" data-id="element-1940">
          <CardContent className="p-6" data-id="element-1941">
            <h3 className="font-heading text-base font-bold mb-1" data-id="element-1942">
              Kontak Helpdesk
            </h3>
            <p className="text-xs text-muted-foreground mb-4" data-id="element-1943">
              Tim dukungan 24/7 untuk pengguna VVIP.
            </p>
            <div className="space-y-3" data-id="element-1944">
              <ContactRow icon={PhoneCall} label="Hotline BSSN" value="0800-GARUDA (4278-32)" onClick={() => toast.info('Menyambungkan ke helpdesk...')} data-id="element-1945" />
              <ContactRow icon={Mail} label="Email" value="support@garuda-shield.id" onClick={() => toast.info('Membuka email client...')} data-id="element-1946" />
              <ContactRow icon={MessageSquare} label="Live Chat" value="Tersedia 24 jam" onClick={() => toast.info('Membuka live chat...')} data-id="element-1947" />
            </div>
            <div className="mt-5 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-[11px] leading-relaxed" data-id="element-1948">
              <strong data-id="element-1949">Darurat:</strong> Untuk insiden CRITICAL, hubungi Command
              Center langsung di ext. 911.
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Release Notes Dialog */}
      <Dialog open={releaseNotesOpen} onOpenChange={setReleaseNotesOpen} data-id="element-1950">
        <DialogContent className="sm:max-w-[640px] border-border/50 bg-background/95 backdrop-blur" data-id="element-1951">
          <DialogHeader data-id="element-1952">
            <DialogTitle className="font-mono text-sm uppercase tracking-wider flex items-center gap-2" data-id="element-1953">
              <FileText className="size-4 text-primary" data-id="element-1954" />
              Release Notes — GARUDA SHIELD C4I
            </DialogTitle>
            <DialogDescription className="text-xs" data-id="element-1955">
              Riwayat pembaruan sistem. Versi terbaru dirilis untuk seluruh
              tenant VVIP.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-3" data-id="element-1956">
            <div className="space-y-5 py-2" data-id="element-1957">
              {releaseNotes.map(rn => <div key={rn.version} className="border-l-2 border-border/50 pl-4 relative" data-id="element-1958">
                  <div className="absolute -left-1.5 top-1 size-3 rounded-full bg-primary border-2 border-background" data-id="element-1959" />
                  <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-1960">
                    <h4 className="font-heading text-base font-bold" data-id="element-1961">
                      {rn.version}
                    </h4>
                    {rn.tag && <Badge variant="outline" className={`font-mono text-[9px] ${rn.tag === 'CURRENT' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-id="element-1962">
                        {rn.tag}
                      </Badge>}
                    <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1963">
                      · {rn.date}
                    </span>
                  </div>
                  <div className="space-y-3 mt-2" data-id="element-1964">
                    {rn.sections.map(s => <div key={s.type} data-id="element-1965">
                        <p className={`font-mono text-[10px] uppercase tracking-wider mb-1 flex items-center gap-1.5 ${s.color}`} data-id="element-1966">
                          <s.icon className="size-3" data-id="element-1967" />
                          {s.type}
                        </p>
                        <ul className="space-y-1 ml-1" data-id="element-1968">
                          {s.items.map((item, i) => <li key={i} className="text-xs text-muted-foreground leading-relaxed flex items-start gap-2" data-id="element-1969">
                              <span className="text-muted-foreground/60 mt-0.5" data-id="element-1970">
                                ·
                              </span>
                              <span data-id="element-1971">{item}</span>
                            </li>)}
                        </ul>
                      </div>)}
                  </div>
                </div>)}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>;
}
function ContactRow({
  icon: Icon,
  label,
  value,
  onClick
}: {
  icon: any;
  label: string;
  value: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="w-full text-left p-3 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/40 hover:border-primary/40 transition flex items-center gap-3" data-id="element-1972">
      <div className="size-9 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0" data-id="element-1973">
        <Icon className="size-4 text-primary" data-id="element-1974" />
      </div>
      <div className="min-w-0" data-id="element-1975">
        <p className="font-mono text-[10px] uppercase text-muted-foreground" data-id="element-1976">
          {label}
        </p>
        <p className="text-sm font-medium truncate" data-id="element-1977">{value}</p>
      </div>
    </button>;
}